import { useReducer } from "react";
import BadgeCharityContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../utils/helpers/execute";
import EndPoints from "../../services/end-points";
import { ICreateBadgeCharity, IUpdateBadgeCharity } from "../../models/badge_charity/request";
import { IBadgeCharity } from "../../models/badge_charity/response";
import { IBadgeCharityQuery } from "../../models/badge_charity/query";

export interface IProps {
  children: React.ReactNode;
}

const BadgeCharityContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
        const { data } = await EndPoints.badgeCharity.getAllBadgeCharities(state.query);
        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
        const { data } = await EndPoints.badgeCharity.getBadgeCharity(id);
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const setDetails = async (data?: IBadgeCharity) => {
    dispatch({ type: "SET_DETAILS", payload: { details: data } });
  };

  const createBadgeCharity = async (request: ICreateBadgeCharity) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
        await EndPoints.badgeCharity.createBadgeCharity(request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
      },
      throwException: true,
    });
  };

  const updateBadgeCharity = async (id: number, request: IUpdateBadgeCharity) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.badgeCharity.updateBadgeCharity(id, request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const deleteBadgeCharity = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
        await EndPoints.badgeCharity.deleteBadgeCharity(id);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IBadgeCharityQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  return (
    <BadgeCharityContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDetails,
          setDetails,
          createBadgeCharity,
          updateBadgeCharity,
          deleteBadgeCharity,
          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </BadgeCharityContext.Provider>
  );
};

export default BadgeCharityContextProvider;